import { selectCurrentTeam } from 'containers/app/appSlice';
import { useLazyGetEstimatesQuery } from 'containers/estimates/api';
import { EstimateListItem } from 'containers/estimates/estimates.model';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FileTypes } from 'types/baseTypes';
import { isPermissionDenied } from 'utils/apiUtils';
import { useAppSelector } from '../../../hooks/typedHooks';
import { EstimateStatus, estimateSearchTypes } from '../constants';

export interface EstimateQueryParams {
  status: EstimateStatus[];
  memberIds: string[];
  customerIds: string[];
  fromCreatedAt: string;
  toCreatedAt: string;
  fromExpiresAt: string;
  toExpiresAt: string;
  search: string;
  type: FileTypes;
  moreInfo: boolean;
  amount: string;
  leadSource: string[];
  assignees: string[];
}

export const initialQueryParamsState: EstimateQueryParams = {
  status: [EstimateStatus.PENDING],
  memberIds: [],
  customerIds: [],
  fromCreatedAt: null,
  toCreatedAt: null,
  fromExpiresAt: null,
  toExpiresAt: null,
  search: null,
  type: null,
  moreInfo: true,
  amount: null,
  leadSource: [],
  assignees: []
};

export const usePaginatedGetEstimatesQuery = (limit = 50) => {
  const [list, setList] = useState<EstimateListItem[]>([]);
  const [queryParams, setQueryParams] = useState<EstimateQueryParams>(initialQueryParamsState);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [hasPermissionsToView, setHasPermissionsToView] = useState(true);
  const page = useRef(1);
  const [getEstimatesData, estimatesState] = useLazyGetEstimatesQuery();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const [searchType, setSearchType] = useState(estimateSearchTypes[0]);

  const isInitListCall: boolean = page.current === 1;

  useEffect(() => {
    if (!estimatesState?.isFetching && estimatesState.isSuccess) {
      setHasPermissionsToView(true);
      const { estimates = [], metadata } = estimatesState?.data?.data ?? { estimates: [], metadata: {} };
      const updatedList = estimates || [];

      setList(updatedList);

      if ((estimates && estimates.length < limit) || (metadata && metadata.count === 0)) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }

      setIsLoading(false);
    }
  }, [estimatesState?.isFetching]);

  useEffect(() => {
    if (!estimatesState?.isFetching && estimatesState?.isError) {
      setIsLoading(false);
      setHasMore(false);
      if (isPermissionDenied(estimatesState.error?.['data']?.code, estimatesState.error?.['data']?.message)) {
        setHasPermissionsToView(false);
      }
    }
  }, [estimatesState?.isError]);

  const resetListAndLoadMore = () => {
    setList([]);
    loadMore(1);
  };

  useEffect(() => {
    resetListAndLoadMore();
  }, [queryParams]);

  const loadMore = useCallback(
    (currentPageNumber: number) => {
      setIsLoading(true);
      const isSearchPresent = queryParams.search !== null && queryParams.search !== '';
      if (currentTeam?.id) {
        page.current = currentPageNumber;
        const { fromCreatedAt, toCreatedAt, fromExpiresAt, toExpiresAt, search, ...restQueryParams } = queryParams;
        try {
          getEstimatesData({
            teamId: currentTeam?.id,
            limit: limit,
            page: page.current - 1,
            fromCreatedAt: fromCreatedAt ? new Date(new Date(fromCreatedAt).toUTCString()).toISOString() : null,
            toCreatedAt: toCreatedAt ? new Date(new Date(toCreatedAt).setHours(23, 59, 59, 999)).toISOString() : null,
            fromExpiresAt: fromExpiresAt ? new Date(new Date(fromExpiresAt).toUTCString()).toISOString() : null,
            toExpiresAt: toExpiresAt ? new Date(new Date(toExpiresAt).setHours(23, 59, 59, 999)).toISOString() : null,
            ...restQueryParams,
            [searchType.type]: isSearchPresent ? search.trim() : null
          }).unwrap();
        } catch (err) {
          setIsError(true);
        }
      }
    },
    [currentTeam?.id, page.current, queryParams]
  );

  const resetQuery = () => {
    resetListAndLoadMore();
  };

  return {
    isLoading: isLoading,
    isFetching: estimatesState?.isFetching,
    totalListCount: estimatesState?.data?.data?.metadata?.count ?? list.length,
    list,
    isError,
    loadMore,
    resetQuery,
    hasMore,
    setQueryParams,
    queryParams,
    isInitListCall,
    isLoadingSuccess: estimatesState.isSuccess,
    hasPermissionsToView,
    searchType,
    setSearchType,
    page: page.current
  };
};
